/**
 * Admin Analytics Page - Olympic Fit Wellness
 */
import { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  Users,
  DollarSign,
  Calendar,
  Activity,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('30d');

  const metrics = [
    { label: 'Total Revenue', value: '$4.2M', change: '+12.5%', positive: true, icon: DollarSign },
    { label: 'Active Members', value: '52,431', change: '+8.2%', positive: true, icon: Users },
    { label: 'Class Attendance', value: '45,890', change: '+15.3%', positive: true, icon: Calendar },
    { label: 'Churn Rate', value: '2.1%', change: '-0.5%', positive: true, icon: Activity }
  ];

  const topClasses = [
    { name: 'Power HIIT', bookings: 2450, fill: 92 },
    { name: 'Yoga Flow', bookings: 2100, fill: 85 },
    { name: 'Power Spin', bookings: 1890, fill: 78 },
    { name: 'Strength & Sculpt', bookings: 1650, fill: 70 },
    { name: 'Boxing Fundamentals', bookings: 1420, fill: 65 }
  ];

  const locationPerformance = [
    { name: 'Frisco', revenue: '$1.5M', members: 15670, growth: '+18%' },
    { name: 'Dallas', revenue: '$1.35M', members: 14081, growth: '+10%' },
    { name: 'McKinney', revenue: '$1.2M', members: 12450, growth: '+15%' },
    { name: 'Plano', revenue: '$980K', members: 10230, growth: '+12%' }
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Analytics</h1>
          <p className="text-neutral-400">Business performance metrics</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="12m">Last 12 months</option>
        </select>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="bg-neutral-800 rounded-xl p-6 border border-neutral-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                <metric.icon className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <span className={`flex items-center gap-1 text-sm font-medium ${
                metric.positive ? 'text-green-400' : 'text-red-400'
              }`}>
                {metric.change}
                {metric.positive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              </span>
            </div>
            <div className="text-3xl font-heading font-bold text-white mb-1">{metric.value}</div>
            <div className="text-sm text-neutral-400">{metric.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Classes */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700">
          <div className="p-6 border-b border-neutral-700">
            <h2 className="text-xl font-heading font-bold text-white">Top Classes</h2>
            <p className="text-sm text-neutral-400">By total bookings this month</p>
          </div>
          <div className="p-6 space-y-4">
            {topClasses.map((cls, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{cls.name}</span>
                  <span className="text-neutral-400">{cls.bookings.toLocaleString()} bookings</span>
                </div>
                <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#3B82F6] rounded-full"
                    style={{ width: `${cls.fill}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Location Performance */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700">
          <div className="p-6 border-b border-neutral-700">
            <h2 className="text-xl font-heading font-bold text-white">Location Performance</h2>
            <p className="text-sm text-neutral-400">Revenue and membership by location</p>
          </div>
          <div className="divide-y divide-neutral-700">
            {locationPerformance.map((loc, idx) => (
              <div key={idx} className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-white">Olympic Fit {loc.name}</h3>
                  <p className="text-sm text-neutral-400">{loc.members.toLocaleString()} members</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-white">{loc.revenue}</p>
                  <p className="text-sm text-green-400">{loc.growth}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-6">
        <h2 className="text-xl font-heading font-bold text-white mb-6">Revenue Trend</h2>
        <div className="h-64 flex items-center justify-center border border-dashed border-neutral-700 rounded-lg">
          <div className="text-center text-neutral-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-2" />
            <p>Revenue chart visualization</p>
            <p className="text-sm">Connect to analytics service for live data</p>
          </div>
        </div>
      </div>
    </div>
  );
}
